import { useState, useEffect, useCallback } from "react";
import {
  fetchApprovedVersions,
  fetchGlobalApprovedVersions,
  type ApprovedVersionItem,
} from "../services/contractTemplateService";

export type UseApprovedVersionsReturn = {
  readonly versions: ApprovedVersionItem[];
  readonly total: number;
  readonly isLoading: boolean;
  readonly reload: () => void;
};

export function useApprovedVersions(
  contractId: string | undefined,
  sectionId: string | null,
  sectionName?: string | null,
  contractType?: string | null
): UseApprovedVersionsReturn {
  const [versions, setVersions] = useState<ApprovedVersionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [trigger, setTrigger] = useState(0);

  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    if (!contractId || !sectionId) {
      setVersions([]);
      setTotal(0);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const fetcher =
      sectionName && contractType
        ? fetchGlobalApprovedVersions(sectionName, contractType, { limit: 50 })
        : fetchApprovedVersions(contractId, sectionId, { limit: 50 });

    fetcher
      .then((result) => {
        if (!cancelled) {
          setVersions(result.items);
          setTotal(result.total);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVersions([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contractId, sectionId, sectionName, contractType, trigger]);

  return { versions, total, isLoading, reload };
}
